/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { LinearProgress, Card, Stack, Box, IconButton } from '@mui/material';
import type { FeatureCollection, Geometry } from 'geojson';
import { isEqual } from 'lodash';
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { AlertBanner, dateUtils } from '@opengeoweb/shared';
import type { Coordinates } from '@opengeoweb/shared';
import type { CoreAppStore, syncGroupsTypes } from '@opengeoweb/store';
import {
  genericSelectors,
  mapSelectors,
  mapActions,
  genericActions,
  syncGroupsActions,
  useSyncgroupTimeState,
} from '@opengeoweb/store';
import { Close } from '@opengeoweb/theme';
import { webmapUtils } from '@opengeoweb/webmap';
import { MapFeatureClass } from '@opengeoweb/webmap-react';
import { SharedMouseYProvider } from './SharedMouseYProvider';
import { SortableAccordions } from './SortableAccordions';
import { SoundingsLegend } from './SoundingsLegend';
import { getTimeMismatchInfo } from './timeMismatchUtils';
import {
  useGetCollectionLocations,
  useMultipleSources,
  useTimesteps,
} from '../../api/edr-api/hooks';
import type { SoundingDiagramType } from '../../api/edr-api/types';
import { soundingsActions } from '../../store/reducer';
import {
  getDiagramType,
  getSelectedTime,
  getSelectedSources,
  getLocationSelections,
  getSoundingPanelsInOrder,
  getShouldHideLocationMarkers,
} from '../../store/selectors';
import type { SoundingsStore, SoundingsModuleState } from '../../store/types';
import { SoundingsActionOrigin } from '../../store/types';
import { useSoundingsTranslation } from '../../utils/i18n';
import { MenuBarConnect } from '../MenuBar/MenuBarConnect';
import { SoundingsTimeSliderConnect } from '../SoundingsTimeSlider';
import { TIMESLIDER_HEIGHT } from '../SoundingsTimeSlider/SoundingsTimeSlider';

interface SoundingsConnectProps {
  viewId: string;
  soundingsPreset?: SoundingsStore;
}

const useGetSize = (viewRef: React.RefObject<HTMLDivElement>): number => {
  const [size, setSize] = React.useState(0);

  React.useLayoutEffect(() => {
    const container = viewRef.current;
    if (!container) {
      return undefined;
    }

    const OFFSET_HEIGHT = 100; // Space reserved in layout
    const resizeObserver = new ResizeObserver((entries) => {
      if (!entries.length) {
        return;
      }
      const { width, height } = entries[0].contentRect;
      const availableHeight = height - TIMESLIDER_HEIGHT + OFFSET_HEIGHT;
      const nextSize = Math.floor(Math.min(width, availableHeight));
      setSize(nextSize > 0 ? nextSize : 0);
    });

    resizeObserver.observe(container);
    return (): void => {
      resizeObserver.disconnect();
    };
  }, [viewRef]);

  return size;
};

export const SoundingsConnect = ({
  viewId,
  soundingsPreset,
}: SoundingsConnectProps): React.ReactElement => {
  const { t } = useSoundingsTranslation();
  const viewRef = React.useRef<HTMLDivElement>(null);
  const size = useGetSize(viewRef);

  const geojsonLayerId = React.useRef(webmapUtils.generateLayerId()).current;

  const dispatch = useDispatch();

  const soundingsPresetRef = React.useRef<SoundingsStore | undefined>();
  const presetJustLoadedRef = React.useRef(false);
  const initialLocationsRef = React.useRef<
    Record<string, string | Coordinates | undefined>
  >({});
  const previousLocationsRef = React.useRef<
    Record<string, string | Coordinates | undefined>
  >({});

  React.useLayoutEffect(() => {
    dispatch(soundingsActions.registerSoundingsPanel({ panelId: viewId }));
    if (
      soundingsPreset &&
      !isEqual(soundingsPreset, soundingsPresetRef.current)
    ) {
      soundingsPresetRef.current = soundingsPreset;
      presetJustLoadedRef.current = true;
      initialLocationsRef.current = soundingsPreset.selectedLocations || {};
      previousLocationsRef.current = {};
      dispatch(
        soundingsActions.registerSoundingsPreset({
          panelId: viewId,
          preset: soundingsPreset,
        }),
      );
      setTimeout(() => {
        presetJustLoadedRef.current = false;
        initialLocationsRef.current = {};
      }, 500);
    } else if (!soundingsPreset) {
      soundingsPresetRef.current = undefined;
      initialLocationsRef.current = {};
      previousLocationsRef.current = {};
    }
  }, [dispatch, soundingsPreset, viewId]);

  const selectedTime = useSelector((state: SoundingsModuleState) =>
    getSelectedTime(state, viewId),
  );
  const sourceSelections = useSelector(
    (state: SoundingsModuleState) => getSelectedSources(state, viewId),
    shallowEqual,
  );
  const locationSelections = useSelector(
    (state: SoundingsModuleState) => getLocationSelections(state, viewId),
    shallowEqual,
  );
  const diagramType = useSelector((state: SoundingsModuleState) =>
    getDiagramType(state, viewId),
  );
  const shouldHideLocationMarkers = useSelector((state: SoundingsModuleState) =>
    getShouldHideLocationMarkers(state, viewId),
  );
  const selectedMapIds = useSelector(
    (state: CoreAppStore) => genericSelectors.getLinkedMaps(state, viewId),
    shallowEqual,
  );
  const firstLinkedMapId = selectedMapIds[0];
  const { data: sourceTimesteps } = useTimesteps(sourceSelections);
  const syncgroupTimeState = useSyncgroupTimeState(viewId, 0);
  const syncgroupSelectedTime = useMemo(() => {
    if (
      syncgroupTimeState?.current === undefined ||
      !Number.isFinite(syncgroupTimeState.current)
    ) {
      return undefined;
    }
    return dateUtils.fromUnix(syncgroupTimeState.current).toISOString();
  }, [syncgroupTimeState]);
  const isTimeSynced = !!syncgroupSelectedTime;
  const selectedTimeInUse = syncgroupSelectedTime || selectedTime;
  const timeMismatchInfo = useMemo(() => {
    return getTimeMismatchInfo(
      isTimeSynced,
      selectedTimeInUse,
      sourceTimesteps,
    );
  }, [isTimeSynced, selectedTimeInUse, sourceTimesteps]);
  const mismatchKey = timeMismatchInfo ? selectedTimeInUse || '' : '';
  const [isTimeMismatchDismissed, setIsTimeMismatchDismissed] =
    React.useState(false);

  const setChosenDiagramType = React.useCallback(
    (diagramType: SoundingDiagramType) => {
      dispatch(
        soundingsActions.updateDiagramType({
          diagramType,
          panelId: viewId,
          origin: SoundingsActionOrigin.soundingsComponent,
        }),
      );
    },
    [dispatch, viewId],
  );

  const selectedFeature = useSelector(
    (state: CoreAppStore) =>
      genericSelectors.getSelectedFeature(state.syncGroups!, viewId),
    shallowEqual,
  );
  const selectedMapPin = useSelector(
    (state: CoreAppStore) =>
      // mapPins are synced so we can arbitrarily choose the first linked map
      mapSelectors.getPinLocation(state, firstLinkedMapId),
    shallowEqual,
  );

  interface CollectionItem {
    id: string;
    url: string;
    name: string;
  }
  const collectionsRef = React.useRef<CollectionItem[]>();
  const collections = useMemo((): CollectionItem[] => {
    const newCollections = sourceSelections.map(
      ({ collectionId, url, name }) => ({
        id: collectionId,
        url,
        name: name || collectionId,
      }),
    );
    if (isEqual(newCollections, collectionsRef.current)) {
      return collectionsRef.current!;
    }
    collectionsRef.current = newCollections;
    return newCollections;
  }, [sourceSelections]);

  const { data: rawLocationData } = useGetCollectionLocations(
    collections,
    true,
  );

  // Stabilize locationData to prevent infinite loops caused by new array references
  const locationDataRef = React.useRef<typeof rawLocationData>();
  const locationData = useMemo(() => {
    if (isEqual(rawLocationData, locationDataRef.current)) {
      return locationDataRef.current;
    }
    locationDataRef.current = rawLocationData;
    return rawLocationData;
  }, [rawLocationData]);

  /* Make sure the map pin is visible when linking new maps */
  useEffect(() => {
    const toggleMapPinVisibility = (displayMapPin: boolean): void => {
      selectedMapIds.forEach((mapId) => {
        dispatch(
          mapActions.toggleMapPinIsVisible({
            mapId,
            displayMapPin,
          }),
        );
      });
    };

    toggleMapPinVisibility(true);

    return (): void => {
      // Hide the map pin when the component is unmounted
      toggleMapPinVisibility(false);
    };
  }, [dispatch, selectedMapIds]);

  // construct and send geojson to map for drawing locations
  const geoJsonForMap = useMemo<
    FeatureCollection<Geometry, syncGroupsTypes.PotentialDataProperties>
  >(
    () => ({
      type: 'FeatureCollection',
      features: (locationData || []).flatMap(({ id: serviceId, features }) =>
        features.map((feature) => ({
          type: 'Feature' as const,
          geometry: feature.geometry,
          id: feature.id,
          properties: {
            name: feature.properties.name,
            mapFeatureClass: MapFeatureClass.ObservationStation,
            highlightSelectedFeature: selectedFeature?.id === feature.id,
            id: feature.id,
            serviceId,
            hidden: shouldHideLocationMarkers,
          },
        })),
      ),
    }),
    [locationData, selectedFeature?.id, shouldHideLocationMarkers],
  );

  useEffect(() => {
    dispatch(
      genericActions.addSharedData({
        panelId: viewId,
        data: {
          features: [
            {
              id: geojsonLayerId,
              geoJSON: geoJsonForMap,
            },
          ],
        },
      }),
    );
  }, [dispatch, geojsonLayerId, geoJsonForMap, viewId]);

  useEffect(
    () => (): void => {
      if (viewId) {
        dispatch(syncGroupsActions.deleteSharedData({ panelId: viewId }));
      }
    },
    [dispatch, viewId],
  );

  // Map location selection has changed. Update soundings location
  // since the location menu may be out of sync
  useEffect(() => {
    if (presetJustLoadedRef.current) {
      return;
    }

    const serviceId = selectedFeature?.serviceId;
    const featureId = selectedFeature?.id;

    if (serviceId && featureId) {
      const currentLocation = locationSelectionsRef.current[serviceId];
      const initialLocation = initialLocationsRef.current[serviceId];
      if (currentLocation !== featureId && initialLocation !== featureId) {
        dispatch(
          soundingsActions.updateSelectedLocation({
            sourceId: serviceId,
            location: featureId,
            panelId: viewId,
            origin: SoundingsActionOrigin.soundingsComponent,
          }),
        );
      }
    }
  }, [dispatch, selectedFeature?.id, selectedFeature?.serviceId, viewId]);

  // Use ref to avoid circular dependency when reading locationSelections
  const locationSelectionsRef = React.useRef(locationSelections);
  React.useEffect(() => {
    locationSelectionsRef.current = locationSelections;
  }, [locationSelections]);

  // Makes position requests for all user-selected sources for the current map pin position.
  // Map pin change on its own implies a new position selection.
  useEffect(() => {
    if (presetJustLoadedRef.current) {
      return;
    }

    if (
      !selectedFeature?.id &&
      !selectedMapPin?.id &&
      selectedMapPin?.lon &&
      selectedMapPin.lat
    ) {
      const newLocation: [number, number] = [
        selectedMapPin.lon,
        selectedMapPin.lat,
      ];

      // Update location for all selected sources
      sourceSelections.forEach((source) => {
        if (source?.collectionId) {
          const currentLocation =
            locationSelectionsRef.current[source.collectionId];
          const isSameLocation =
            Array.isArray(currentLocation) &&
            currentLocation[0] === newLocation[0] &&
            currentLocation[1] === newLocation[1];

          if (!isSameLocation) {
            dispatch(
              soundingsActions.updateSelectedLocation({
                sourceId: source.collectionId,
                location: newLocation,
                panelId: viewId,
                origin: SoundingsActionOrigin.soundingsComponent,
              }),
            );
          }
        }
      });
    }
  }, [selectedMapPin, selectedFeature?.id, sourceSelections, dispatch, viewId]);

  useEffect(() => {
    if (!locationData || locationData.length === 0) {
      return;
    }
    if (selectedMapIds.length === 0) {
      return;
    }

    let changedSourceId: string | undefined;
    for (const source of sourceSelections) {
      if (source?.collectionId) {
        const currentLocation = locationSelections[source.collectionId];
        const previousLocation =
          previousLocationsRef.current[source.collectionId];

        if (currentLocation !== previousLocation) {
          changedSourceId = source.collectionId;
          if (!presetJustLoadedRef.current) {
            break;
          }
        }
      }
    }

    if (!changedSourceId) {
      return;
    }

    const selectedLocation = locationSelections[changedSourceId];
    if (!selectedLocation) {
      return;
    }

    previousLocationsRef.current = {
      ...previousLocationsRef.current,
      [changedSourceId]: selectedLocation,
    };

    const serviceData = locationData.find(
      (service) => service.id === changedSourceId,
    );
    if (!serviceData) {
      return;
    }

    if (typeof selectedLocation === 'string') {
      const foundFeature = serviceData.features.find(
        (feature) => feature.id === selectedLocation,
      );
      if (foundFeature?.geometry.type === 'Point') {
        const [lon, lat] = foundFeature.geometry.coordinates;
        const mapPinNeedsUpdate =
          selectedMapPin?.id !== selectedLocation ||
          selectedMapPin?.lon !== lon ||
          selectedMapPin?.lat !== lat;
        const featureNeedsUpdate = selectedFeature?.id !== selectedLocation;

        if (mapPinNeedsUpdate) {
          selectedMapIds.forEach((mapId) => {
            dispatch(
              mapActions.setMapPinLocation({
                mapId,
                mapPinLocation: { lon, lat, id: selectedLocation },
              }),
            );
          });
        }

        if (featureNeedsUpdate) {
          dispatch(
            genericActions.addSharedData({
              panelId: viewId,
              data: { selectedFeatureId: selectedLocation },
            }),
          );
        }
      }
    } else if (Array.isArray(selectedLocation)) {
      const [lon, lat] = selectedLocation;
      const needsUpdate =
        selectedMapPin?.lon !== lon || selectedMapPin?.lat !== lat;

      if (needsUpdate) {
        selectedMapIds.forEach((mapId) => {
          dispatch(
            mapActions.setMapPinLocation({
              mapId,
              mapPinLocation: { lon, lat },
            }),
          );
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    locationData,
    locationSelections,
    sourceSelections,
    selectedMapIds,
    dispatch,
    viewId,
  ]);

  const { error, data, isPending } = useMultipleSources(
    sourceSelections,
    locationSelections,
    selectedTimeInUse,
    'exact',
  );

  useEffect(() => {
    setIsTimeMismatchDismissed(false);
  }, [mismatchKey]);

  const soundingPanelsInOrder = useSelector((store: SoundingsModuleState) =>
    getSoundingPanelsInOrder(store, viewId),
  );

  const changeOrder = (
    newOrder: { id: string; name: string; isDisplayed: boolean }[],
  ): void => {
    const orderedIds = newOrder.map((collection) => collection.id);
    dispatch(
      soundingsActions.changeSoundingPanelOrder({
        panelId: viewId,
        newOrderOfIds: orderedIds,
        origin: SoundingsActionOrigin.soundingsComponent,
      }),
    );
  };

  return (
    <>
      {!!error && <AlertBanner shouldClose title={error.message} />}
      {!!isPending && (
        <LinearProgress
          color="secondary"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            zIndex: 20,
          }}
        />
      )}
      <Stack
        sx={{
          padding: '12px',
          width: '100%',
          height: '100%',
          backgroundColor: 'geowebColors.background.surfaceApp',
          position: 'relative',
        }}
      >
        <Box paddingLeft={5}>
          <MenuBarConnect viewId={viewId} />
        </Box>
        <Card sx={{ width: '100%', height: '100%', position: 'relative' }}>
          <Stack
            spacing={1}
            sx={{ width: '100%', height: '100%', display: 'flex' }}
          >
            {!!timeMismatchInfo && !isTimeMismatchDismissed && (
              <Box sx={{ flex: '0 0 auto', position: 'relative' }}>
                <AlertBanner
                  severity="warning"
                  title={t('soundings-time-mismatch-title')}
                />
                <IconButton
                  aria-label="Close warning"
                  onClick={(): void => setIsTimeMismatchDismissed(true)}
                  size="small"
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: 10,
                    padding: 0.25,
                    zIndex: 1,
                  }}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            )}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                paddingTop: '2px',
                paddingRight: '2px',
                minHeight: '24px',
                width: '100%',
              }}
            >
              <SoundingsLegend sourceSelections={sourceSelections} />
            </Box>
            <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
              <SharedMouseYProvider>
                <SortableAccordions
                  chosenDiagramType={diagramType}
                  data={data}
                  onOrderChange={changeOrder}
                  setChosenDiagramType={setChosenDiagramType}
                  size={size}
                  soundingPanelsInOrder={soundingPanelsInOrder}
                  viewRef={viewRef}
                />
              </SharedMouseYProvider>
            </Box>
            <Box
              sx={{
                width: '100%',
                flex: '0 0 auto',
                height: `${TIMESLIDER_HEIGHT}px`,
              }}
            >
              <SoundingsTimeSliderConnect
                syncEnabled={isTimeSynced}
                timeInUse={selectedTimeInUse}
                viewId={viewId}
              />
            </Box>
          </Stack>
        </Card>
      </Stack>
    </>
  );
};
